/**
 * @fileoverview Reusable component to display patient prescription history
 * Can be used in both patient detail modal and prescription form
 */
import React, { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Box, Typography, Chip, Grid, Tooltip, CircularProgress } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DmsTable, DmsModal, DmsButton, intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import { PRESCRIPTION_STATUS } from "../../constants/PrescriptionStatus";
import { getIndicationText } from "../../constants/Indication";
import { isExpired } from "../../utils/expiration";
import { formatNotDispensedJustification } from "../../constants/NotDispensedReasonEnum";

/**
 * Get status chip based on prescription status
 */
const getStatusChip = (status, expirationDate) => {
  const expired = isExpired(expirationDate);

  if (status === PRESCRIPTION_STATUS.CANCELED) {
    return <Chip label={intl.get("Canceled").d("Canceled")} color="error" size="small" />;
  }
  if (status === PRESCRIPTION_STATUS.DISPENSED) {
    return <Chip label={intl.get("Dispensed").d("Dispensed")} color="success" size="small" />;
  }
  if (expired) {
    return <Chip label={intl.get("Expired").d("Expired")} color="warning" size="small" />;
  }
  return <Chip label={intl.get("Active").d("Active")} color="primary" size="small" />;
};

/**
 * Patient Prescription History Tab Component
 * Displays a paginated list of all prescriptions for a given patient
 */
export const PatientPrescriptionHistoryTab = ({ patientPk = null }) => {
  const table = useRef();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleViewDetails = useCallback(async (row) => {
    setLoadingDetail(true);
    setDetailModalOpen(true);
    try {
      const response = await fetchData(`/prescription/${row.pk}`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setSelectedPrescription(response.data?.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching prescription details:", error);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedPrescription(null);
  }, []);

  if (!patientPk) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="text.secondary">
          {intl.get("SelectPatientFirst").d("Select a patient first")}
        </Typography>
      </Box>
    );
  }

  const header = [
    {
      label: intl.get("PrescriptionNumber").d("Prescription #"),
      column_name: "prescription_identifier",
    },
    {
      label: intl.get("PrescriptionDate").d("Prescription Date"),
      column_name: "prescription_date",
      element: (row) => row.prescription_date
        ? dayjs(row.prescription_date).format("DD/MM/YYYY")
        : "-",
    },
    {
      label: intl.get("Dosage").d("Dosage"),
      column_name: "dosage_description",
    },
    {
      label: intl.get("Doctor").d("Doctor"),
      column_name: "doctor_name",
      element: (row) => (
        <Box>
          {row.doctor_name || "-"}
          {row.doctor_crm && (
            <Typography variant="caption" display="block" color="text.secondary">
              CRM: {row.doctor_crm.crm}/{row.doctor_crm.crm_state}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      element: (row) => getStatusChip(row.status, row.expiration_date),
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      sortable: false,
      element: (row) => (
        <Tooltip title={intl.get("ViewDetails").d("View Details")}>
          <span>
            <DmsButton
              icon={<VisibilityIcon />}
              onClick={() => handleViewDetails(row)}
              variant="secondary"
              size="small"
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <DmsTable
        header={header}
        managed={true}
        needs_authentication={true}
        ref={table}
        url={`/prescription/patient/${patientPk}/table`}
        export_table={false}
      />

      {/* Detail Modal */}
      <DmsModal
        isVisible={detailModalOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DmsModal.Header
          title={intl.get("PrescriptionDetails").d("Prescription Details")}
          onClose={handleCloseDetail}
        />
        <DmsModal.Body>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedPrescription && (
            <Box sx={{ p: 2 }}>
              {/* Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h6">
                  {intl.get("Prescription").d("Prescription")} {selectedPrescription.prescription_identifier}
                </Typography>
                {getStatusChip(selectedPrescription.status, selectedPrescription.expiration_date)}
              </Box>

              {/* Prescription Section */}
              <Box sx={{ mb: 2, p: 2, bgcolor: "primary.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {intl.get("PrescriptionData").d("Prescription Data")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">{intl.get("PrescriptionDate").d("Prescription Date")}</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">
                      {selectedPrescription.prescription_date ? dayjs(selectedPrescription.prescription_date).format("DD/MM/YYYY") : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">{intl.get("ExpirationDate").d("Expiration Date")}</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">
                      {selectedPrescription.expiration_date ? dayjs(selectedPrescription.expiration_date).format("DD/MM/YYYY") : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">{intl.get("Dosage").d("Dosage")}</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="body2">
                      {selectedPrescription.dosage_description || "-"}
                    </Typography>
                  </Grid>
                  {selectedPrescription.dosage_notes && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("AdditionalNotes").d("Additional Notes")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2" sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                          {selectedPrescription.dosage_notes}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.indication && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("Indication").d("Indication")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">
                          {getIndicationText(selectedPrescription.indication)}
                        </Typography>
                      </Grid>
                      {selectedPrescription.indication_other && (
                        <>
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">{intl.get("OtherIndication").d("Other Indication")}</Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                              {selectedPrescription.indication_other}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  {selectedPrescription.pregnancy_test_date && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("LastPregnancyTestDate").d("Last Pregnancy Test Date")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">
                          {dayjs(selectedPrescription.pregnancy_test_date).format("DD/MM/YYYY")}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.medication && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("Medication").d("Medication")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">
                          {selectedPrescription.medication.name}
                          {selectedPrescription.medication.strength && ` - ${selectedPrescription.medication.strength}${selectedPrescription.medication.strength_unit || ""}`}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.organization_branch && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("Institution").d("Institution")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">
                          {selectedPrescription.organization_branch.name || "-"}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.dosage_quantity != null && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("Quantity").d("Quantity")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">{selectedPrescription.dosage_quantity}</Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.dosage_duration != null && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("Duration").d("Duration")} ({intl.get("Days").d("days")})</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">{selectedPrescription.dosage_duration}</Typography>
                      </Grid>
                    </>
                  )}
                  {selectedPrescription.dosage_pause_days != null && (
                    <>
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">{intl.get("PauseDays").d("Pause Days")}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body2">{selectedPrescription.dosage_pause_days}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>

              {/* Doctor Section */}
              {selectedPrescription.doctor && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {intl.get("Doctor").d("Doctor")}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">{intl.get("Name").d("Name")}</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedPrescription.doctor.person?.dms_user?.user_name || selectedPrescription.doctor.person?.name || "-"}
                      </Typography>
                    </Grid>
                    {selectedPrescription.doctor_crm && (
                      <>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">CRM</Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography variant="body2">
                            {selectedPrescription.doctor_crm.crm}/{selectedPrescription.doctor_crm.crm_state}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Terms Section */}
              <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {intl.get("Terms").d("Terms")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={9}>
                    <Typography variant="caption" color="text.secondary">{intl.get("accept_liability_waiver").d("Liability waiver accepted")}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={selectedPrescription.liability_waiver_signed ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}
                      color={selectedPrescription.liability_waiver_signed ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant="caption" color="text.secondary">{intl.get("AllowAlternativeDispensation").d("Allow alternative dispensation")}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={selectedPrescription.allow_alternative_dispensation ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}
                      color={selectedPrescription.allow_alternative_dispensation ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Dispensation Section */}
              {selectedPrescription.status === PRESCRIPTION_STATUS.DISPENSED && selectedPrescription.dispensing && selectedPrescription.dispensing.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {intl.get("DispensationData").d("Dispensation Data")}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">{intl.get("DispensationDate").d("Dispensation Date")}</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body2">
                        {selectedPrescription.dispensing[0]?.created_on
                          ? dayjs(selectedPrescription.dispensing[0].created_on).format("DD/MM/YYYY")
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">{intl.get("box_count").d("Box Count")}</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body2">{selectedPrescription.dispensing[0]?.box_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">{intl.get("capsule_count").d("Capsule Count")}</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body2">{selectedPrescription.dispensing[0]?.capsule_count || 0}</Typography>
                    </Grid>
                    {selectedPrescription.dispensing[0]?.dispensed_dose_strength && (
                      <>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">{intl.get("DoseStrength").d("Dose Strength")}</Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography variant="body2">{selectedPrescription.dispensing[0].dispensed_dose_strength}mg</Typography>
                        </Grid>
                      </>
                    )}
                    {selectedPrescription.dispensing[0]?.batch?.number && (
                      <>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">{intl.get("Batch").d("Batch")}</Typography>
                        </Grid>
                        <Grid item xs={9}>
                          <Typography variant="body2">{selectedPrescription.dispensing[0].batch.number}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Cancellation Section */}
              {selectedPrescription.cancellation_justification && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "error.50", borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="error.main" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {intl.get("CancellationReason").d("Cancellation Reason")}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">{intl.get("Details").d("Details")}</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography variant="body2">
                        {formatNotDispensedJustification(selectedPrescription.cancellation_justification)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton
            text={intl.get("Close").d("Close")}
            onClick={handleCloseDetail}
          />
        </DmsModal.Footer>
      </DmsModal>
    </Box>
  );
};

PatientPrescriptionHistoryTab.propTypes = {
  patientPk: PropTypes.number,
};

export default PatientPrescriptionHistoryTab;
